// #18929 — the central date/time formatter. It renders in the signed-in user's
// preferred timezone (see ./userTimeZone) so the Settings → Account → Timezone
// preference actually changes the times shown to them. Everything that should
// honour that preference formats through here rather than calling
// `toLocale*String()` (which always uses the browser's zone) directly.

import { getUserTimeZone } from './userTimeZone';

export type DateInput = Date | string | number | null | undefined;

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DATETIME_DEFAULT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
};

const DATE_DEFAULT: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

/**
 * Format a date/time in the user's preferred timezone. Invalid/empty input
 * returns `''`. If the zone is somehow rejected at format time, falls back to
 * the browser's zone so a time always renders.
 */
export function formatDateTime(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value);
  if (!d) return '';
  const opts = options ?? DATETIME_DEFAULT;
  try {
    return new Intl.DateTimeFormat(undefined, { timeZone: getUserTimeZone(), ...opts }).format(d);
  } catch {
    return new Intl.DateTimeFormat(undefined, opts).format(d);
  }
}

/** Date-only, in the user's preferred timezone. */
export function formatDate(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  return formatDateTime(value, options ?? DATE_DEFAULT);
}
